import { isObject } from '../SharedUtils.js'
import { Field, FieldType, FieldGroup, } from '../Common/Field.js'
import { ComponentArray } from '../Common/ComponentArray.js'
import { getLabel } from '../Common/Units.js'

/**
 * Snapshot of a single Field's state at collection time.
 */
export class FieldInfo {
  constructor(field, fullPath, uiPath) {
    this.fullPath = fullPath;
    this.uiPath = uiPath;
    this.name = field.name;
    this.type = field.type;
    // Use the get*() accessors, not the raw value/displayValue/defaultValue
    // properties: subclasses like FieldWithDefault override these to return
    // their effective (possibly computed) value, and reading the raw
    // properties directly would silently show stale or wrong data.
    this.value = field.getValue();
    this.displayValue = field.getDisplayValue();
    this.displayUnits = field.getDisplayUnitsLabel();
    this.enabled = field.enabled;
    this.isOutput = field.isOutput;
    this.min = field.min;
    this.max = field.max;
    this.required = field.requiresInput;
    this.defaultValue = field.getDefaultValue();
    this.note = field.note;
    this.notesData = field.notesData;

    if (field.type == FieldType.Select || field.type == FieldType.TypeSelect) {
      // getChoices(), not the raw .choices property: some Select fields (e.g.
      // WallLayer.category) only set a dynamic choicesFunc, never a static
      // .choices array, so reading .choices directly would always be empty.
      this.choices = field.getChoices();
    } else if (field.type == 'MultiTieredSelect') {
      this.choices = field.getAllChoices();
    }

    // FieldWithVariableUnits (e.g. Summer Infiltration: cfm, cfm/ft² wall
    // area, cfm/ft crack length, or ACH) — the raw unit constants its
    // applyAgentValue({value, units}) accepts, paired with a human label.
    if (field.unitOptions) {
      this.unitOptions = field.unitOptions.map(u => ({ value: u, label: getLabel(u) }));
    }
  }

  // Trims this down to what read_project actually needs to send the agent.
  // Dropped: uiPath (only used for the client's own proposal preview, built
  // separately — see propose_changes in Tools.js); min/max/defaultValue/note/
  // notesData (static per component type, now documented once in the
  // component-schemas docs instead of repeated on every field of every
  // instance — see src/AgentDocs/GenerateComponentSchemas.js).
  //
  // value: displayValue when present (numeric fields only — see
  // Field._syncDisplayValue, which never sets it for Select/String/
  // MultiTieredSelect), else the raw value. This is deliberate, not just
  // trimming: it's also exactly the value propose_changes expects back as
  // new_value (ChangerTool uses setDisplayValueAsUser() for numeric fields),
  // so echoing raw `value` here risked the agent reading one unit system and
  // writing back in another.
  toAgentJson() {
    const compact = {
      fullPath: this.fullPath,
      name: this.name,
      type: this.type,
      value: this.displayValue ?? this.value,
    };
    if (this.displayUnits) compact.units = this.displayUnits;
    if (!this.enabled) compact.enabled = false;
    if (this.isOutput) compact.isOutput = true;
    if (this.required) compact.required = true;
    // This field's unit is switchable — e.g. {"value": 10, "units": "ACH"} to
    // set 10 air changes/hour instead of the cfm shown above.
    if (this.unitOptions) compact.unitOptions = this.unitOptions;
    // Select choices are usually small and can be genuinely project-specific
    // (e.g. WallLayer.category once custom materials exist), so keep them
    // inline. MultiTieredSelect choices are the opposite — both real usages
    // (Space Type, Appliance Type) are driven by fixed global data tables,
    // can run into the hundreds of entries, and are already fully documented
    // once in that componentType's schema doc — repeating the same list on
    // every instance is pure duplication, not per-instance state.
    if (this.choices && this.type !== 'MultiTieredSelect') compact.choices = this.choices;
    return compact;
  }
}

// JSON.stringify replacer that compacts every FieldInfo in the tree via
// toAgentJson(). Use this (not a plain JSON.stringify(snapshot)) wherever a
// snapshot is being sent to the AI — see read_project in Tools.js.
export function agentJsonReplacer(key, value) {
  return value instanceof FieldInfo ? value.toAgentJson() : value;
}

// A cheap, flat list of every item across the project's top-level
// ComponentArray sections (systems, spaces, wallTypes, ... for Commercial;
// walls, roofs, wallTypes, ... for Residential) — {path, name, componentType},
// no field data. Lets the agent discover what a project contains, and which
// paths to pass to read_project's `paths` param, without paying for the full
// (potentially huge) per-instance data on every call.
// Items without a name field (e.g. a Residential project's walls/roofs/floors/
// partitions) get a positional label like "Wall 1", matching how residential
// results refer to them.
// Deliberately shallow: only top-level sections, not e.g. zones nested
// inside a system — see read_project's description for the addressing
// convention (a bare section key like "/wallTypes" vs an indexed item like
// "/systems[0]").
export function buildComponentsIndex(snapshot) {
  const index = [];
  for (const [key, node] of Object.entries(snapshot.userInputs)) {
    if (!node || node.type !== 'ComponentArray') continue;
    node.values.forEach((elem, i) => {
      const name = elem?.name instanceof FieldInfo ? elem.name.value : `${node.componentType} ${i + 1}`;
      index.push({ path: `/${key}[${i}]`, name, componentType: node.componentType });
    });
  }
  return index;
}

// Parses a requested top-level path: "/systems[0]" -> {key: 'systems', index: 0},
// or a bare "/wallTypes"/"/buildingAndEnv" -> {key: 'wallTypes', index: null}
// (whole section). Returns null for anything else (e.g. a deeper path like
// "/spaces[0]/walls[1]" — read_project's `paths` only scopes top-level
// sections/items, not arbitrary depth).
function parseTopLevelPath(path) {
  const match = /^\/([^[/]+)(?:\[(\d+)\])?$/.exec(path);
  if (!match) return null;
  return { key: match[1], index: match[2] !== undefined ? Number(match[2]) : null };
}

// Prunes a snapshot down to just the requested top-level sections/items (see
// parseTopLevelPath for the two forms `paths` entries can take). Unresolvable
// entries (a deeper path, or a section this project type doesn't have, e.g.
// "/spaces" in a Residential project) are skipped rather than failing the
// whole call, and returned as `unknownPaths` so the agent can tell "not
// supported/doesn't exist" apart from "empty".
export function filterSnapshotToPaths(snapshot, paths) {
  const wantedByKey = new Map(); // key -> 'all' | Set<index>
  const unknownPaths = [];
  for (const path of paths) {
    const parsed = parseTopLevelPath(path);
    if (!parsed || !(parsed.key in snapshot.userInputs)) {
      unknownPaths.push(path);
      continue;
    }
    const { key, index } = parsed;
    if (index === null) {
      wantedByKey.set(key, 'all');
      continue;
    }
    const existing = wantedByKey.get(key);
    if (existing === 'all') continue;
    const set = existing instanceof Set ? existing : new Set();
    set.add(index);
    wantedByKey.set(key, set);
  }

  const userInputs = {};
  for (const [key, wanted] of wantedByKey) {
    const node = snapshot.userInputs[key];
    if (!node) continue;
    if (wanted === 'all' || node.type !== 'ComponentArray') {
      userInputs[key] = node;
    } else {
      userInputs[key] = { ...node, values: node.values.filter((_, i) => wanted.has(i)) };
    }
  }
  return { userInputs, unknownPaths };
}

/**
 * Traverses a project and returns a nested structure that mirrors the project
 * hierarchy, with Field instances replaced by FieldInfo objects.
 * Objects map to objects, arrays map to arrays.
 */
export class FieldCollector {
  // Returns [snapshot, componentsMap, uiPathMap] where:
  //   snapshot       - nested object mirroring the project, Fields replaced by FieldInfo
  //   componentsMap  - Map<fullPath, Field|ComponentArray> for direct lookup by path
  //   uiPathMap      - Map<fullPath, uiPath> for human-readable path lookup
  static collect(project) {
    const componentsMap = new Map();
    const uiPathMap = new Map();
    const userInputs = FieldCollector._collectObj(project, new Set(), '', '', componentsMap, uiPathMap);
    const snapshot = { userInputs };
    return [snapshot, componentsMap, uiPathMap];
  }

  // Resolve objInfo._name, which may be a string or a function.
  // Falls back to `fallback` if not set.
  static _resolveName(obj, fallback) {
    const raw = obj?.objInfo?._name
    if (typeof raw === 'function') return raw()
    return raw ?? fallback
  }

  // UI label for the i-th element of an array. Elements without a name field
  // (e.g. a Residential project's walls, or a wall's windows) all share the
  // same static objInfo._name, so the position is appended ("Wall 2") to keep
  // proposal previews for different elements apart.
  static _resolveElemName(elem, i) {
    const name = FieldCollector._resolveName(elem, null)
    if (name === null) return `[${i}]`
    return elem.name instanceof Field ? name : `${name} ${i + 1}`
  }

  static _collectObj(obj, visited, curPath, curUiPath, componentsMap, uiPathMap) {
    if (visited.has(obj)) return {};
    visited.add(obj);

    const result = {};
    // Lets a non-Field component (e.g. LocationData) inject computed/summary
    // keys into its own snapshot, alongside its normal Field-walked properties.
    // See src/Components/LocationData.js for the motivating example.
    if (typeof obj.getAgentSummary === 'function') {
      Object.assign(result, obj.getAgentSummary());
    }
    for (const key of Object.keys(obj)) {
      // A leading underscore marks a property as internal/non-owned — e.g.
      // WallLayer._materialInfo is a cached lookup of a CustomWallMaterial
      // that's really owned by componentsLibrary.customMaterials. Walking it
      // here would both be redundant and, since it's the same object
      // instance, incorrectly trip the `visited` cycle-guard for the real
      // owning path, making that path collect as empty.
      if (key.startsWith('_')) continue;
      const child = obj[key];
      const childPath = `${curPath}/${key}`;
      if (child instanceof Field) {
        const childUiPath = `${curUiPath}/${child.name}`
        result[key] = new FieldInfo(child, childPath, childUiPath);
        componentsMap.set(childPath, child);
        uiPathMap.set(childPath, childUiPath);
      } else if (child instanceof ComponentArray) {
        const componentType = child.cls.name;
        componentsMap.set(childPath, child);
        const childUiPath = `${curUiPath}/${FieldCollector._resolveName(child, key)}`
        uiPathMap.set(childPath, childUiPath);
        result[key] = {
          type: 'ComponentArray',
          componentType,
          values: child
            .filter(elem => isObject(elem) && elem.childObjs === '$auto')
            .map((elem, i) => {
              const elemPath = `${childPath}[${i}]`
              const elemUiPath = `${childUiPath}/${FieldCollector._resolveElemName(elem, i)}`
              uiPathMap.set(elemPath, elemUiPath)
              return FieldCollector._collectObj(elem, visited, elemPath, elemUiPath, componentsMap, uiPathMap)
            }),
        };
      } else if (Array.isArray(child)) {
        const childUiPath = `${curUiPath}/${FieldCollector._resolveName(child, key)}`
        result[key] = child
          .filter(elem => isObject(elem) && elem.childObjs === '$auto')
          .map((elem, i) => {
            const elemUiPath = `${childUiPath}/${FieldCollector._resolveElemName(elem, i)}`
            return FieldCollector._collectObj(elem, visited, `${childPath}[${i}]`, elemUiPath, componentsMap, uiPathMap)
          });
      } else if (child instanceof FieldGroup) {
        const childUiPath = `${curUiPath}/${FieldCollector._resolveName(child, key)}`
        uiPathMap.set(childPath, childUiPath);
        const groupResult = {};
        for (const field of child.fields) {
          const fieldPath = `${childPath}/${field.key}`;
          const fieldUiPath = `${childUiPath}/${field.name}`;
          groupResult[field.key] = new FieldInfo(field, fieldPath, fieldUiPath);
          componentsMap.set(fieldPath, field);
          uiPathMap.set(fieldPath, fieldUiPath);
        }
        result[key] = groupResult;
      } else if (isObject(child) && child.childObjs === '$auto') {
        const childUiPath = `${curUiPath}/${FieldCollector._resolveName(child, key)}`
        // Register the object itself (not just its fields) if it supports
        // whole-object writes, so its own path is addressable by propose_changes.
        if (typeof child.applyAgentValue === 'function') {
          componentsMap.set(childPath, child);
          uiPathMap.set(childPath, childUiPath);
        }
        result[key] = FieldCollector._collectObj(child, visited, childPath, childUiPath, componentsMap, uiPathMap);
      }
    }
    return result;
  }
}
